import { BusinessCategory, OrganizationDemographics, UseCase } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';

import confetti from 'canvas-confetti';
import { getMainPage } from '../helpers/page-name';
import organizationInfo from '../helpers/state/organization-info';
import { setLocalStorage } from '../helpers/utils';
import { updateOrganizationDemographics } from '../helpers/api/organization-api';

/**
 * Organization Profile Popup
 * Gathers user information about their industry, use cases, and organization website
 */
@customElement('organization-profile-popup')
export class OrganizationProfilePopup extends LitElement {
    static amplitude_callsite = 'Organization Profile Popup';

    @state()
    private selectedBusinessCategories: BusinessCategory[] = [];

    @state()
    private selectedUseCases: UseCase[] = [];

    @state()
    private website: string = '';

    @state()
    private teamSize: '1' | '2-5' | '5-10' | '20+' | '' = '';

    @state()
    private isSubmitting: boolean = false;

    @state()
    private showThankYou: boolean = false;

    private async handleSubmit() {
        if (!organizationInfo.organization) {
            return;
        }

        this.isSubmitting = true;

        try {
            const organizationId = organizationInfo.organization._id.$oid;

            const demographics: OrganizationDemographics = {
                business_category: this.selectedBusinessCategories.length > 0 ? this.selectedBusinessCategories : undefined,
                use_cases: this.selectedUseCases.length > 0 ? this.selectedUseCases : undefined,
                website: this.website || undefined,
                team_size: this.teamSize || undefined,
            };

            // Use the new updateOrganizationDemographics API
            await updateOrganizationDemographics(
                OrganizationProfilePopup.amplitude_callsite,
                organizationId,
                demographics
            );

            // Refresh organization data
            await organizationInfo.instantiateOrganizationDataAsAuthenticatedUser(organizationId);

            // Mark as permanently completed since they filled it out
            setLocalStorage(`org_profile_completed_${organizationId}`, 'permanent');

            // Show thank you message
            this.showThankYou = true;
            await confetti();

            // Auto-close after 2 seconds
            setTimeout(() => {
                this.dispatchEvent(new CustomEvent('close'));
            }, 2000);
        } catch (error) {
            console.error('Failed to update organization profile', error);
            getMainPage().errorToastElement.showToast('Failed to update organization profile');
        } finally {
            this.isSubmitting = false;
        }
    }

    private handleSkip() {
        this.dispatchEvent(new CustomEvent('close'));
    }

    demographicsChanged(e: CustomEvent) {
        const demographics: OrganizationDemographics = e.detail;
        this.selectedBusinessCategories = demographics.business_category ?? [];
        this.selectedUseCases = demographics.use_cases ?? [];
        this.website = demographics.website ?? '';
        this.teamSize = demographics.team_size ?? '';
    }

    render() {
        if (this.showThankYou) {
            return html`
                <div class="popup-container">
                    <div class="thank-you-content">
                        <div class="thank-you-icon">✓</div>
                        <h2 class="thank-you-title">Thank you!</h2>
                        <p class="thank-you-message">Your profile has been updated successfully.</p>
                    </div>
                </div>
            `;
        }

        return html`
            <div class="popup-container">
                <div class="popup-content">
                    <h2 class="popup-title">Help us understand your needs</h2>
                    <p class="popup-subtitle">This helps us provide better recommendations and features for your workspace.</p>

                    <demographics-form
                        .selectedBusinessCategories=${this.selectedBusinessCategories}
                        .selectedUseCases=${this.selectedUseCases}
                        .website=${this.website}
                        .teamSize=${this.teamSize}
                        @demographics-changed="${this.demographicsChanged}"
                    >
                    </demographics-form>

                    <div class="button-row">
                        <form-button
                            variant="outline"
                            size="md"
                            @click=${this.handleSkip}
                            ?disabled=${this.isSubmitting}
                        >
                            Skip
                        </form-button>
                        <form-button
                            variant="raised"
                            size="md"
                            @click=${this.handleSubmit}
                            ?disabled=${this.isSubmitting}
                        >
                            ${this.isSubmitting ? 'Saving...' : 'Save'}
                        </form-button>
                    </div>
                </div>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
                overflow: auto;
            }

            .popup-container {
                padding: 1.5rem;
                max-width: 600px;
                margin: 0 auto;
            }

            .popup-content {
                display: flex;
                flex-direction: column;
                gap: 1.25rem;
            }

            .popup-title {
                margin: 0;
                font-size: 1.25rem;
                font-weight: 600;
                color: var(--main-primary-color);
                text-align: left;
                line-height: 1.5rem;
            }

            .popup-subtitle {
                margin: 0;
                font-size: 0.875rem;
                color: var(--theme-input-color);
                line-height: 1.25rem;
            }

            .form-section {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }

            .form-label {
                font-size: 0.875rem;
                font-weight: 600;
                color: var(--main-primary-color);
            }

            .use-cases-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
                gap: 0.5rem;
                margin-top: 0.25rem;
            }

            .use-case-pill {
                padding: 0.5rem 0.75rem;
                border: 1px solid var(--theme-border-color);
                border-radius: 4px;
                font-size: 0.75rem;
                text-align: center;
                cursor: pointer;
                transition: border-color 0.2s ease, background-color 0.2s ease;
                background-color: var(--theme-white-color);
                color: var(--main-primary-color);
                line-height: 1rem;
            }

            .use-case-pill:hover {
                border-color: var(--main-primary-color);
            }

            .use-case-pill.selected {
                background-color: var(--main-success-color);
                color: var(--theme-white-color);
                border-color: var(--main-success-color);
            }

            .button-row {
                display: flex;
                justify-content: flex-end;
                gap: 0.75rem;
                margin-top: 0.75rem;
                padding-top: 1rem;
                border-top: 1px solid var(--theme-border-color);
            }

            proxi-selectfield {
                --select-height: 2.5rem;
            }

            proxi-textfield {
                --textfield-height: 2.5rem;
            }

            .thank-you-content {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                padding: 2rem 1rem;
                gap: 1rem;
            }

            .thank-you-icon {
                width: 4rem;
                height: 4rem;
                border-radius: 50%;
                background-color: var(--main-success-color);
                color: var(--theme-white-color);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 2rem;
                font-weight: bold;
                animation: scaleIn 0.3s ease-out;
            }

            .thank-you-title {
                margin: 0;
                font-size: 1.5rem;
                font-weight: 600;
                color: var(--main-primary-color);
            }

            .thank-you-message {
                margin: 0;
                font-size: 0.875rem;
                color: var(--theme-input-color);
            }

            @keyframes scaleIn {
                from {
                    transform: scale(0);
                    opacity: 0;
                }
                to {
                    transform: scale(1);
                    opacity: 1;
                }
            }

            @media (max-width: 550px) {
                .popup-container {
                    padding: 1rem;
                }

                .popup-title {
                    font-size: 1rem;
                    line-height: normal;
                }

                .popup-subtitle {
                    font-size: 0.8rem;
                }

                .use-cases-grid {
                    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
                }

                .button-row {
                    flex-direction: column-reverse;
                    gap: 0.5rem;
                }

                .button-row form-button {
                    width: 100%;
                }
            }
        `;
    }
}
